import React, { useEffect, useMemo, useState } from "react";
import style from "./style.module.css";
import { useSelector } from "react-redux";
import DynamicHTMLComponent from "../../../../components/DynamicHTMLComponent";
import axios from "axios";
import ModalJobs from "../ModalJobs";
import ModalSuccess from "../ModalSuccess";
import ModalError from "../ModalError";
import lang from "../../../../lang/lang";
import { trackGenerateLead } from "../../../../analytics";

const MAX_CV_BYTES = 2 * 1024 * 1024;
const ALLOWED_EXT = ["pdf", "doc", "docx", "xls", "xlsx", "csv", "docm"];

const JobsItem = ({ openSubList, index, activeIndex, item }) => {
  const isOpen = index === activeIndex;
  const { currentLanguage } = useSelector(({ main }) => main);
  const t = useMemo(
    () => lang[currentLanguage] || lang.hy || {},
    [currentLanguage]
  );

  const [userCv, setUserCv] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [inlineError, setInlineError] = useState("");
  const [errorSendBtnStyles, setErrorSendBtnStyles] = useState(false);
  const [buttonText, setButtonText] = useState("");
  const [sending, setSending] = useState(false);

  const showError = (message) => {
    const msg = message || t.cv_error_generic || t.success_resume;
    setErrorMessage(msg);
    setInlineError(msg);
    setErrorSendBtnStyles(true);
    setOpenModalError(true);
  };

  const validateFile = (file) => {
    if (!file) {
      return t.cv_file_required || t.attach_cv;
    }
    const name = (file.name || "").toLowerCase();
    const ext = name.includes(".") ? name.split(".").pop() : "";
    if (!ALLOWED_EXT.includes(ext)) {
      return t.cv_file_mimes;
    }
    if (file.size > MAX_CV_BYTES) {
      return t.cv_file_too_large;
    }
    return "";
  };

  const handleChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    if (!selectedFile) {
      setUserCv(null);
      return;
    }
    const err = validateFile(selectedFile);
    if (err) {
      setUserCv(null);
      event.target.value = "";
      showError(err);
      return;
    }
    setUserCv(selectedFile);
    setErrorSendBtnStyles(false);
    setInlineError("");
    setErrorMessage("");
  };

  useEffect(() => {
    if (userCv === null) {
      setButtonText(t.attach_cv || "Attach CV");
    } else {
      setButtonText(userCv.name);
    }
  }, [userCv, isOpen, index, t.attach_cv]);

  const extractApiMessage = (data) => {
    if (!data) return "";
    if (typeof data.message === "string" && data.message.trim()) {
      return data.message;
    }
    const codeMap = {
      file_required: "cv_file_required",
      file_mimes: "cv_file_mimes",
      file_too_large: "cv_file_too_large",
      job_required: "cv_file_required",
      generic: "cv_error_generic",
    };
    if (data.code && codeMap[data.code] && t[codeMap[data.code]]) {
      return t[codeMap[data.code]];
    }
    // Laravel validation bag: { file: ["..."], job_id: ["..."] }
    if (data.message && typeof data.message === "object") {
      const firstKey = Object.keys(data.message)[0];
      const val = firstKey ? data.message[firstKey] : null;
      if (Array.isArray(val) && val[0]) return String(val[0]);
      if (typeof val === "string") return val;
    }
    if (data.errors && typeof data.errors === "object") {
      const firstKey = Object.keys(data.errors)[0];
      const val = firstKey ? data.errors[firstKey] : null;
      if (Array.isArray(val) && val[0]) return String(val[0]);
    }
    return "";
  };

  const postData = async (e) => {
    e.preventDefault();
    if (sending) return;

    const clientErr = validateFile(userCv);
    if (clientErr) {
      showError(clientErr);
      return;
    }

    const formData = new FormData();
    try {
      const _token = document.querySelector('meta[name="_token"]')?.content;
      formData.append("login", "VlvShopA");
      formData.append("password", "oL0rG6vQ6k");
      formData.append("token", "quHRhfW9R7SbNFUCEBEBmzCRbIa5Z3");
      formData.append("job_id", item.id);
      formData.append("locale", currentLanguage || "hy");
      formData.append("file", userCv);

      setSending(true);
      const response = await axios.post(
        process.env.REACT_APP_SERVER_URL + "api/job/send-cv",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-CSRF-Token": _token,
            "X-Locale": currentLanguage || "hy",
          },
          // Let us read 413/422 bodies
          validateStatus: () => true,
        }
      );

      if (response?.data?.success === true || response?.data?.error === 0) {
        trackGenerateLead({ lead_type: "job", form_name: "job_cv" });
        setUserCv(null);
        setButtonText(t.attach_cv || "Attach CV");
        setInlineError("");
        setErrorSendBtnStyles(false);
        setOpenModal(true);
        return;
      }

      const apiMsg =
        extractApiMessage(response?.data) ||
        (response?.status === 413
          ? t.cv_file_too_large
          : t.cv_error_generic);
      showError(apiMsg);
    } catch (error) {
      const status = error?.response?.status;
      const apiMsg =
        extractApiMessage(error?.response?.data) ||
        (status === 413 ? t.cv_file_too_large : t.cv_error_generic);
      showError(apiMsg);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div
        className={
          !isOpen ? style.job_accordion_item : style.job_accordion_item_active
        }>
        <div
          onClick={() => openSubList(index)}
          className={style.job_accordion_item_title}>
          <span>{item["name_" + currentLanguage]}</span>

          {!isOpen ? (
            <span className={style.job_accordion_plus}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M9 0V9M9 9V18M9 9H18M9 9H0"
                  stroke="#282828"
                  strokeWidth="3"></path>
              </svg>
            </span>
          ) : (
            <span className={style.job_accordion_minus}>
              <svg
                width="18"
                height="4"
                viewBox="0 0 18 4"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path d="M18 2H9H0" stroke="#282828" strokeWidth="3"></path>
              </svg>
            </span>
          )}
        </div>
        <div className={style.accordion_sub_hidden_list}>
          <DynamicHTMLComponent itemHtml={item["job_" + currentLanguage]} />
          <form className={style.accordion_pdf_save} onSubmit={postData}>
            <div className={style.accordion_files_block}>
              <label
                className={
                  errorSendBtnStyles
                    ? style.file_label_error + " " + style.file_label
                    : style.file_label
                }
                htmlFor={`pdf_${index + 1}`}
                id="changeImg">
                {buttonText}
              </label>
              <input
                onChange={handleChange}
                id={`pdf_${index + 1}`}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.docm,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                name="file"
              />
              <div className={style.file_hint}>{t.cv_hint}</div>
              {inlineError ? (
                <div className={style.file_error} role="alert">
                  {inlineError}
                </div>
              ) : null}
            </div>
            <button
              type="submit"
              disabled={sending}
              className={style.send_message_btn}>
              {sending ? t.cv_sending || "…" : t.send || "Send"}
            </button>
          </form>
        </div>
      </div>
      {openModal ? (
        <ModalJobs onClose={setOpenModal}>
          <ModalSuccess onClose={setOpenModal} />
        </ModalJobs>
      ) : null}
      {openModalError ? (
        <ModalJobs onClose={setOpenModalError}>
          <ModalError
            onClose={setOpenModalError}
            message={errorMessage || t.cv_error_generic}
          />
        </ModalJobs>
      ) : null}
    </>
  );
};

export default JobsItem;
